import { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Payment = () => {
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const packages = [
    {
      id: 'starter',
      name: 'Starter Package',
      price: '$100',
      period: '/month',
      description: 'Perfect for small businesses starting their LinkedIn journey',
      features: [
        'Profile optimization',
        '10 posts per month',
        'Basic analytics',
        'Email support',
        'Monthly strategy call'
      ]
    },
    {
      id: 'growth',
      name: 'Growth Package',
      price: '$999',
      period: '/month',
      description: 'Ideal for businesses ready to scale their LinkedIn presence',
      features: [
        'Everything in Starter',
        '12 posts per month',
        'Lead generation campaign',
        'Advanced analytics',
        'Priority support',
        'Bi-weekly strategy calls',
        'Content calendar'
      ],
      popular: true
    },
    {
      id: 'enterprise',
      name: 'Enterprise Package',
      price: '$2,499',
      period: '/month',
      description: 'Comprehensive solution for established businesses',
      features: [
        'Everything in Growth',
        'Daily content posting',
        'Multi-account management',
        'Dedicated account manager',
        '24/7 support',
        'Weekly strategy calls',
        'Custom campaigns',
        'Competitor analysis'
      ]
    }
  ];

  const handleCheckout = async (packageId) => {
    setLoading(true);
    setError('');
    setSelectedPackage(packageId);

    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/payments/checkout`, {
        package_id: packageId,
        origin_url: originUrl
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.response?.data?.detail || 'Failed to initiate payment. Please try again.');
      setLoading(false);
      setSelectedPackage(null);
    }
  };

  return (
    <div className="pt-16">
      <section className="py-32 bg-primary-dark text-text-inverse" data-testid="payment-hero">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="inline-block text-secondary-main font-body uppercase tracking-widest text-xs mb-4"
            data-testid="payment-subtitle"
          >
            Pricing Plans
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-heading text-4xl lg:text-6xl font-bold mb-6 leading-tight"
            data-testid="payment-title"
          >
            Choose Your Growth Path
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-text-inverse/80 text-lg max-w-3xl mx-auto leading-relaxed"
            data-testid="payment-description"
          >
            Flexible packages designed to meet your business needs and budget. All plans include our core LinkedIn marketing services.
          </motion.p>
        </div>
      </section>

      <section className="py-20 bg-background-main" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-6">
          {error && (
            <div className="max-w-2xl mx-auto mb-8 p-4 bg-red-50 border border-red-200 text-red-700 rounded" data-testid="payment-error">
              {error}
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`bg-white border p-8 hover:shadow-xl transition-all relative ${
                  pkg.popular ? 'border-secondary-main border-2 shadow-lg' : 'border-primary-main/10'
                }`}
                data-testid={`package-card-${pkg.id}`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2" data-testid="popular-badge">
                    <span className="bg-secondary-main text-primary-dark px-4 py-1 text-xs uppercase tracking-widest font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <h3 className="font-heading text-2xl font-bold text-primary-main mb-2" data-testid={`package-name-${pkg.id}`}>
                  {pkg.name}
                </h3>
                <div className="mb-4">
                  <span className="font-heading text-4xl font-bold text-primary-main" data-testid={`package-price-${pkg.id}`}>{pkg.price}</span>
                  <span className="text-text-muted" data-testid={`package-period-${pkg.id}`}>{pkg.period}</span>
                </div>
                <p className="text-text-secondary text-sm leading-relaxed mb-6" data-testid={`package-description-${pkg.id}`}>
                  {pkg.description}
                </p>
                
                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start gap-2" data-testid={`package-feature-${pkg.id}-${fIndex}`}>
                      <CheckCircle2 className="w-5 h-5 text-secondary-main flex-shrink-0 mt-0.5" />
                      <span className="text-text-secondary text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <button
                  onClick={() => handleCheckout(pkg.id)}
                  disabled={loading && selectedPackage === pkg.id}
                  data-testid={`package-button-${pkg.id}`}
                  className={`w-full py-4 uppercase tracking-widest text-xs font-semibold rounded-none transition-all flex items-center justify-center gap-2 ${
                    pkg.popular
                      ? 'bg-primary-main text-white hover:bg-primary-light'
                      : 'border border-primary-main text-primary-main hover:bg-primary-main hover:text-white'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {loading && selectedPackage === pkg.id ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    'Select Package'
                  )}
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white" data-testid="payment-faq">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl font-bold text-primary-main mb-6" data-testid="faq-heading">
            Questions About Pricing?
          </h2>
          <p className="text-text-secondary leading-relaxed mb-8" data-testid="faq-description">
            We offer flexible payment options and custom packages for larger organizations. Contact us to discuss your specific needs.
          </p>
          <a
            href="/enquiry"
            data-testid="faq-cta"
            className="border border-primary-main text-primary-main hover:bg-primary-main hover:text-white rounded-none px-8 py-4 uppercase tracking-widest text-xs font-semibold inline-block transition-all"
          >
            Contact Sales
          </a>
        </div>
      </section>
    </div>
  );
};

export default Payment;